import React, { useEffect, useState } from "react";
import { Paperclip, Download, Trash2, Loader2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import api, { errMessage } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

const useBlob = (url, enabled) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    let obj; let alive = true;
    api.get(url, { responseType: "blob" })
      .then((r) => { obj = URL.createObjectURL(r.data); if (alive) setSrc(obj); })
      .catch(() => alive && setFailed(true));
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [url, enabled]);
  return { src, failed };
};

export const TicketAttachment = ({ ticketId, att, onChange }) => {
  const base = `/admin/support/tickets/${ticketId}/attachments/${att.id}`;
  const deleted = Boolean(att.deleted_at);
  const { src, failed } = useBlob(`${base}?inline=true`, att.previewable && !deleted);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    try {
      const r = await api.get(base, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error("Couldn't download the attachment."); }
  };
  const remove = async () => {
    setBusy(true);
    try { onChange((await api.delete(base)).data); toast.success("Attachment deleted."); }
    catch (e) { toast.error(errMessage(e)); } finally { setBusy(false); }
  };

  if (deleted) {
    return (
      <div className="mt-2 flex items-center gap-2 rounded-md border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400" data-testid={`attachment-deleted-${att.id}`}>
        <ImageOff className="h-3.5 w-3.5" /> {att.filename} — deleted {att.deleted_reason === "retention" ? "by 90-day retention policy" : "by an admin"}
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-md border border-slate-200 bg-white p-2" data-testid={`attachment-${att.id}`}>
      {att.previewable && (
        <div className="mb-2 flex min-h-[80px] items-center justify-center overflow-hidden rounded bg-slate-50">
          {src ? <img src={src} alt={att.filename} className="max-h-72 w-auto object-contain" data-testid={`attachment-preview-${att.id}`} />
            : failed ? <span className="text-xs text-slate-400">Preview unavailable</span>
              : <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </div>
      )}
      <div className="flex items-center gap-2 text-xs">
        <Paperclip className="h-3.5 w-3.5 text-slate-400" />
        <span className="min-w-0 flex-1 truncate font-medium text-slate-700">{att.filename}</span>
        <span className="text-slate-400">{fmtSize(att.size)}</span>
        <button type="button" onClick={download} className="rounded p-1 text-[#0D9488] hover:bg-slate-100" aria-label="Download" data-testid={`attachment-download-${att.id}`}><Download className="h-3.5 w-3.5" /></button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button type="button" disabled={busy} className="rounded p-1 text-red-600 hover:bg-red-50" aria-label="Delete" data-testid={`attachment-delete-${att.id}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this attachment?</AlertDialogTitle>
              <AlertDialogDescription>The file will be permanently erased and can't be recovered. The message text stays.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="attachment-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="attachment-delete-confirm">Delete permanently</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
